/**
 * About — Plan 07-04 Task 1 GREEN.
 *
 * Closes ABOUT-01 (About page — project, anchor era, London Charter, audience)
 * AND the Phase-4 ↔ Phase-7 dual-phase contract for ABOUT-04 (the
 * September 11, 1923 Robert E. Lee Klan No. 1 mass initiation at Edgewood
 * Lake: factual acknowledgment paragraph with primary-source link,
 * same-visual-weight styling per Pitfall 6, never appears in any 3D scene).
 *
 * Content is authored in `app/src/content/about-copy.ts` so the copy deck is
 * editable without touching the renderer. The renderer here is a shallow map
 * over `aboutCopy.sections` and applies one uniform paragraph style to every
 * paragraph in every section. Pitfall 6 compliance is structural — the Klan
 * paragraph CANNOT be styled differently because every <p> uses the same
 * `S.p` style.
 *
 * AESTHETIC-BRIEF Rule 7 chrome (cream background, espresso text, warm-ochre
 * accents, Goudy Old Style / Cormorant Garamond serif stack) via the shared
 * PeriodChromeStyles tokens.
 */
import { Link } from 'react-router-dom';
import { aboutCopy } from '../content/about-copy';
import { typography, periodChrome } from '../ui/PeriodChromeStyles';

export function About() {
  return (
    <main role="main" aria-labelledby="about-title" style={S.main}>
      <div style={S.inner}>
        <header style={S.header}>
          <nav aria-label="Site navigation" style={S.nav}>
            <Link to="/" style={S.navLink}>
              Experience
            </Link>
            <span style={S.navSep} aria-hidden="true">·</span>
            <Link to="/read" style={S.navLink}>
              Read the Tour
            </Link>
            <span style={S.navSep} aria-hidden="true">·</span>
            <Link to="/sources" style={S.navLink}>
              Sources
            </Link>
            <span style={S.navSep} aria-hidden="true">·</span>
            <Link to="/credits" style={S.navLink}>
              Credits
            </Link>
          </nav>
          <h1 id="about-title" style={S.h1}>
            {aboutCopy.pageTitle}
          </h1>
        </header>

        {aboutCopy.sections.map((section) => (
          <section
            key={section.id}
            aria-labelledby={`${section.id}-heading`}
            style={S.section}
          >
            <h2 id={`${section.id}-heading`} style={S.h2}>
              {section.heading}
            </h2>
            {section.paragraphs.map((p, i) => (
              <p key={i} style={S.p}>
                {p}
              </p>
            ))}
            {section.klanParagraphSourceLink && section.klanParagraphSourceLabel && (
              <p style={S.p}>
                <Link
                  to={section.klanParagraphSourceLink}
                  style={S.sourceLink}
                >
                  {section.klanParagraphSourceLabel}
                </Link>
              </p>
            )}
          </section>
        ))}
      </div>
    </main>
  );
}

const S: Record<string, React.CSSProperties> = {
  main: {
    minHeight: '100vh',
    background: periodChrome.cream,
    color: periodChrome.espresso,
    fontFamily: typography.serif,
    padding: '3rem 1.5rem',
    boxSizing: 'border-box',
    lineHeight: 1.6,
  },
  inner: {
    maxWidth: '72ch',
    margin: '0 auto',
  },
  header: {
    marginBottom: '2rem',
    borderBottom: `1px solid ${periodChrome.ochre}`,
    paddingBottom: '1rem',
  },
  nav: {
    marginBottom: '1rem',
    fontSize: '0.95em',
    opacity: 0.85,
  },
  navLink: {
    color: periodChrome.espresso,
    textDecoration: 'underline',
    textDecorationColor: periodChrome.ochre,
  },
  navSep: {
    margin: '0 0.5rem',
    color: periodChrome.ochre,
  },
  h1: {
    fontWeight: 400,
    fontSize: '2.25rem',
    letterSpacing: '0.04em',
    margin: '0.5rem 0',
    lineHeight: 1.2,
  },
  section: {
    marginBottom: '2rem',
  },
  h2: {
    fontWeight: 400,
    fontSize: '1.35rem',
    marginTop: '1.5rem',
    marginBottom: '0.5rem',
    letterSpacing: '0.02em',
  },
  // Uniform paragraph style — Pitfall 6 same-visual-weight for Klan paragraph.
  p: {
    margin: '0.75rem 0',
    fontSize: '1.0625rem',
    lineHeight: 1.6,
  },
  sourceLink: {
    color: periodChrome.warmOchre,
    textDecoration: 'underline',
    textDecorationColor: periodChrome.ochre,
  },
};
